import React, { useState, useEffect, useRef } from "react";
import { Box, keyframes } from "@mui/material";

/**
 * Slow zoom-in keyframes (each slide zooms in while visible)
 */
const zoomIn = keyframes`
  0% {
    transform: scale(1) translate(0, 0);
  }
  100% {
    transform: scale(1.12) translate(-0.8%, -0.8%);
  }
`;

/**
 * Number of background images
 */
const TOTAL_SLIDES = 10;

/**
 * Build the array of background image URLs
 * @param {string} format - 'webp' or 'jpg'
 */
function getBackgroundImages(format = 'webp') {
  return Array.from({ length: TOTAL_SLIDES }, (_, i) => {
    const num = String(i + 1).padStart(2, "0");
    return `/images/karsanusa/bg/bg-${num}.${format}`;
  });
}

/**
 * Detect if browser supports WebP format
 */
function checkWebPSupport() {
  try {
    const canvas = document.createElement('canvas');
    if (canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0) {
      return true;
    }
  } catch (e) {
    // Canvas not supported
  }
  return false;
}

/**
 * Test if a specific image URL loads successfully
 * Returns a promise that resolves to true if image loads, false if it fails
 */
function testImageLoad(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = src;
  });
}

// Cache WebP support check
let webpSupported = null;

function supportsWebP() {
  if (webpSupported === null) {
    webpSupported = checkWebPSupport();
  }
  return webpSupported;
}

/**
 * Get background images with WebP fallback to JPG
 * Tests first image, if WebP fails, returns all JPG paths
 */
async function getBackgroundImagesWithFallback() {
  const webpImages = getBackgroundImages('webp');
  const jpgImages = getBackgroundImages('jpg');
  
  // If browser doesn't support WebP at all, use JPG
  if (!supportsWebP()) {
    return jpgImages;
  }
  
  // Test if first WebP image loads
  try {
    const canLoadWebP = await testImageLoad(webpImages[0]);
    return canLoadWebP ? webpImages : jpgImages;
  } catch (e) {
    // If test fails, fallback to JPG
    return jpgImages;
  }
}

/**
 * Single zooming slide layer — uses a key to restart animation on image change
 */
function ZoomLayer({ imageSrc, animationKey }) {
  return (
    <Box
      key={animationKey}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundImage: `url(${imageSrc})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        animation: `${zoomIn} 20s ease-out forwards`,
        willChange: "transform, opacity",
      }}
    />
  );
}

/**
 * AuthBackgroundSlideshow Component
 *
 * Full-screen background slideshow for auth pages.
 * Cycles through all images in /images/karsanusa/bg/ with:
 * - Slow zoom-in animation on each slide
 * - Smooth fade-through-black transition between slides
 * - Infinite looping
 *
 * @param {number} [interval=20000] - Duration each slide is visible (ms)
 * @param {number} [overlayOpacity=0.4] - Dark overlay opacity (0-1)
 *
 * @example
 * <AuthBackgroundSlideshow />
 */
export default function AuthBackgroundSlideshow({
  interval = 20000,
  overlayOpacity = 0.4,
}) {
  const [images, setImages] = useState([]);
  const [displayIndex, setDisplayIndex] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const timerRef = useRef(null);
  const animKeyRef = useRef(0);

  // Load images with WebP/JPG fallback
  useEffect(() => {
    getBackgroundImagesWithFallback().then(setImages);
  }, []);

  // Slideshow cycle: fade out → change image → fade in
  useEffect(() => {
    // Don't start slideshow until images are loaded
    if (images.length === 0) return;

    function cycleSlide() {
      timerRef.current = setTimeout(() => {
        // Phase 1: Fade out current slide
        setOpacity(0);

        // Phase 2: After fade completes, switch image
        setTimeout(() => {
          animKeyRef.current += 1; // new key = restart zoom animation
          setDisplayIndex((prev) => (prev + 1) % images.length);

          // Phase 3: Fade back in
          requestAnimationFrame(() => {
            setOpacity(1);
          });

          // Schedule next cycle
          cycleSlide();
        }, 1500);
      }, interval);
    }

    cycleSlide();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [interval, images.length]);

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
        overflow: "hidden",
        bgcolor: "#000",
      }}
    >
      {/* Zooming image layer */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          opacity,
          transition: "opacity 1.5s ease-in-out",
          willChange: "opacity",
        }}
      >
        <ZoomLayer
          imageSrc={images[displayIndex]}
          animationKey={animKeyRef.current}
        />
      </Box>

      {/* Dark overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: `rgba(0, 0, 0, ${overlayOpacity})`,
          zIndex: 1,
          pointerEvents: "none",
        }}
      />
    </Box>
  );
}
